import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Listens for global auth events and performs router navigations accordingly.
 *
 * Currently handles:
 * - "auth:expired" (dispatched by fetch401Interceptor when a 401 happens)
 *   -> Navigates to "/" (Index) so the app shows Login/Dashboard as appropriate.
 *
 * Notes:
 * - We add a small delay before navigating to allow AuthContext to finish
 *   clearing local auth state and toasts to render.
 * - Navigation is throttled to avoid multiple redirects when multiple requests
 *   fail at once.
 */
export function AuthRouteEffects() {
  const navigate = useNavigate();
  const location = useLocation();

  const lastNavRef = useRef<number>(0);
  const COOLDOWN_MS = 3000; // match interceptor throttle to avoid double actions

  useEffect(() => {
    const onAuthExpired = () => {
      const now = Date.now();
      if (now - lastNavRef.current < COOLDOWN_MS) return; // throttle
      lastNavRef.current = now;

      // If we're already at "/", no need to navigate again
      if (location.pathname === "/") return;

      // Allow AuthContext to clear state and show the toast
      window.setTimeout(() => {
        try {
          navigate("/", { replace: true });
        } catch {
          // ignore navigation errors
        }
      }, 80);
    };

    window.addEventListener("auth:expired", onAuthExpired as EventListener);
    return () => {
      window.removeEventListener("auth:expired", onAuthExpired as EventListener);
    };
  }, [location.pathname, navigate]);

  return null;
}
